// src/belediye/pages/sistem/SistemKullaniciDetayPage.js
//
// Sistem Yönetimi · Kullanıcı Detayı — sekmeli düzen.
//  Hero (foto + kimlik + roller + durum + son giriş) + sekmeler:
//   1) Roller & Birimler — üyelik/rol yönetimi (tek rol deneyimi)
//   2) Özel İzinler — birim başına allow/deny (users.assign_permission)
//   3) İzin Simülatörü — 3-renk efektif izin + görünecek sayfalar
//   4) Hesap & Güvenlik — şifre sıfırla (göster) / SMS ile şifre gönder / giriş logları
//  İşlevler korunmuştur; yalnız düzen/tasarım yenilendi + SMS ile şifre gönder eklendi.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLeftIcon,
  PlusIcon,
  XMarkIcon,
  BuildingOffice2Icon,
  KeyIcon,
  AdjustmentsHorizontalIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  EnvelopeIcon,
  PhoneIcon,
  ClockIcon,
  DevicePhoneMobileIcon,
} from "@heroicons/react/24/outline";

import usePermissions from "../../hooks/usePermissions";
import api from "../../services/belediyeApi";
import {
  fetchUnitMemberships,
  fetchRoles,
  fetchSistemUnitsTree,
  flattenUnitTree,
  createMembership,
  updateMembership,
  fetchEffectivePermissions,
  fetchFeatureActions,
  fetchUserLoginLogs,
  resetUserPassword,
  sendPasswordSms,
} from "../../services/sistemApi";
import { visibleNav } from "../../nav";
import TrDateTime from "../../components/utils/TrDateTime";
import { formatPersonNameTr, safeText } from "../../components/utils/textFormat";
import "./SistemKullaniciDetay.css";

const WORKSPACES = ["belediye", "sistem", "halk-masasi", "saha"];

const backendError = (ex, fallback) => {
  const d = ex?.response?.data;
  if (typeof d === "string") return d;
  if (d?.detail) return d.detail;
  if (d) {
    return Object.entries(d)
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
      .join(" · ");
  }
  return fallback;
};
const setsEqual = (a, b) => a.size === b.size && [...a].every((x) => b.has(x));

export default function SistemKullaniciDetayPage() {
  const { userId } = useParams();
  const { can, isSuperUser } = usePermissions();
  const canAssign = can("users.assign_role");
  const canAssignPerm = can("users.assign_permission");
  // FAZ-23 — hesap/güvenlik (Personel'den taşındı)
  const canResetPw = can("users.password.reset");
  const canViewLogs = can("users.logs.view");

  // FAZ-23 hesap/güvenlik state
  const [pwResult, setPwResult] = useState(null);
  const [pwBusy, setPwBusy] = useState(false);
  const [pwErr, setPwErr] = useState("");
  const [logs, setLogs] = useState(null);
  const [logsBusy, setLogsBusy] = useState(false);

  // Sekme + SMS ile şifre gönder
  const [tab, setTab] = useState("roller");
  const [smsBusy, setSmsBusy] = useState(false);
  const [smsMsg, setSmsMsg] = useState(null); // { ok, text }

  const doSendSms = useCallback(async () => {
    setSmsBusy(true);
    setSmsMsg(null);
    try {
      const res = await sendPasswordSms([Number(userId)]);
      if (res?.sent?.length) {
        const phone = res.sent[0].phone ? ` (${res.sent[0].phone})` : "";
        setSmsMsg({ ok: true, text: `Yeni parola SMS ile gönderildi${phone}.` });
      } else {
        setSmsMsg({ ok: false, text: res?.skipped?.[0]?.reason || "SMS gönderilemedi." });
      }
    } catch (ex) {
      setSmsMsg({ ok: false, text: backendError(ex, "SMS gönderilemedi.") });
    } finally {
      setSmsBusy(false);
    }
  }, [userId]);

  const doResetPassword = useCallback(async () => {
    setPwBusy(true);
    setPwErr("");
    try {
      setPwResult(await resetUserPassword(userId));
    } catch (ex) {
      setPwErr(backendError(ex, "Şifre sıfırlanamadı."));
    } finally {
      setPwBusy(false);
    }
  }, [userId]);

  const loadLogs = useCallback(async () => {
    setLogsBusy(true);
    try {
      setLogs(await fetchUserLoginLogs(userId));
    } catch {
      setLogs([]);
    } finally {
      setLogsBusy(false);
    }
  }, [userId]);

  const [user, setUser] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [roles, setRoles] = useState([]);
  const [units, setUnits] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  // yeni üyelik formu
  const [newUnit, setNewUnit] = useState("");
  const [newRole, setNewRole] = useState("");
  const [adding, setAdding] = useState(false);

  // efektif izin simülatörü
  const [previewUnit, setPreviewUnit] = useState("");
  const [preview, setPreview] = useState(null); // effective-permissions düz kod listesi
  const [previewLoading, setPreviewLoading] = useState(false);

  // özel izinler için seçili üyelik
  const [ozelUnit, setOzelUnit] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [uRes, mRes, rRes, treeRes, aRes] = await Promise.all([
        api.get(`/common/users/${userId}/`).then((x) => x.data),
        fetchUnitMemberships(),
        fetchRoles(),
        fetchSistemUnitsTree(),
        fetchFeatureActions(),
      ]);
      setUser(uRes);
      setMemberships(mRes.filter((m) => String(m.user) === String(userId)));
      setRoles(rRes);
      setUnits(flattenUnitTree(treeRes));
      setActions(Array.isArray(aRes) ? aRes : []);
    } catch {
      setError("Kullanıcı bilgileri yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  // Hesap & Güvenlik sekmesi açılınca logları bir kez getir
  useEffect(() => {
    if (tab === "guvenlik" && canViewLogs && logs === null && !logsBusy) {
      loadLogs();
    }
  }, [tab, canViewLogs, logs, logsBusy, loadLogs]);

  const unitName = useMemo(() => {
    const map = Object.fromEntries(units.map((u) => [String(u.id), u.name]));
    return (id) => map[String(id)] || `Birim #${id}`;
  }, [units]);

  const rolesByCode = useMemo(
    () => Object.fromEntries(roles.map((r) => [r.code, r])),
    [roles]
  );

  const mutate = async (fn, okMsg) => {
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      await load();
      return true;
    } catch (ex) {
      toast.error(backendError(ex, "İşlem tamamlanamadı."));
      return false;
    }
  };

  const changeRole = async (m, code) => {
    setBusyId(m.id);
    await mutate(() => updateMembership(m.id, { role: code || null }), "Rol güncellendi.");
    setBusyId(null);
  };

  const toggleActive = async (m) => {
    setBusyId(m.id);
    await mutate(
      () => updateMembership(m.id, { is_active: !m.is_active }),
      m.is_active ? "Üyelik pasifleştirildi." : "Üyelik etkinleştirildi."
    );
    setBusyId(null);
  };

  const addMembership = async (e) => {
    e.preventDefault();
    if (!newUnit) return;
    setAdding(true);
    const ok = await mutate(
      () => createMembership({ user: Number(userId), unit: Number(newUnit), role: newRole || null }),
      "Birim/rol atandı."
    );
    if (ok) {
      setNewUnit("");
      setNewRole("");
    }
    setAdding(false);
  };

  const runPreview = async () => {
    setPreviewLoading(true);
    setPreview(null);
    try {
      const res = await fetchEffectivePermissions(userId, previewUnit || undefined);
      setPreview(res?.permissions || []);
    } catch (ex) {
      toast.error(backendError(ex, "Önizleme alınamadı."));
    } finally {
      setPreviewLoading(false);
    }
  };

  // 3-renk kırılımı — seçili birimin üyeliğinden (rolden yeşil / eklenen mavi / kapatılan kırmızı)
  const breakdown = useMemo(() => {
    const m = memberships.find((x) => String(x.unit) === String(previewUnit));
    const roleCodes = new Set(rolesByCode[m?.role]?.permissions || []);
    const allow = m?.allow_actions || [];
    const deny = m?.deny_actions || [];
    const denySet = new Set(deny);
    return {
      hasMembership: !!m,
      green: [...roleCodes].filter((c) => !denySet.has(c)).sort(), // rolden, etkin
      blue: allow.filter((c) => !roleCodes.has(c)).sort(), // kişiye eklenen
      red: [...deny].sort(), // kapatılan
    };
  }, [memberships, previewUnit, rolesByCode]);

  const previewPages = useMemo(() => {
    if (!Array.isArray(preview)) return [];
    const seen = new Map();
    WORKSPACES.forEach((w) => {
      visibleNav(preview, { workspace: w, menuOnly: false }).forEach((it) => {
        if (!seen.has(it.page)) seen.set(it.page, it);
      });
    });
    const byGroup = new Map();
    for (const it of seen.values()) {
      if (!byGroup.has(it.group)) byGroup.set(it.group, []);
      byGroup.get(it.group).push(it);
    }
    return [...byGroup.entries()];
  }, [preview]);

  const ozelMembership = memberships.find((m) => String(m.id) === String(ozelUnit));

  if (loading)
    return (
      <div className="skd-root">
        <div className="skd-state">Yükleniyor…</div>
      </div>
    );
  if (error)
    return (
      <div className="skd-root">
        <div className="skd-state skd-state-error">
          {error}{" "}
          <button className="btn-md btn-md-ghost" onClick={load}>
            Tekrar dene
          </button>
        </div>
      </div>
    );

  const avatarInitial =
    (formatPersonNameTr(user) || user?.username || "?").trim().slice(0, 1).toLocaleUpperCase("tr");

  const TABS = [
    { key: "roller", label: "Roller & Birimler", Icon: BuildingOffice2Icon },
    ...(canAssignPerm ? [{ key: "ozel", label: "Özel İzinler", Icon: KeyIcon }] : []),
    { key: "simulator", label: "İzin Simülatörü", Icon: AdjustmentsHorizontalIcon },
    ...(canResetPw || canViewLogs
      ? [{ key: "guvenlik", label: "Hesap & Güvenlik", Icon: ShieldCheckIcon }]
      : []),
  ];
  const activeTab = TABS.some((t) => t.key === tab) ? tab : "roller";

  return (
    <div className="skd-root ukd-root">
      <Link to="/yonetim/sistem/kullanicilar" className="skd-back">
        <ArrowLeftIcon width={18} height={18} /> Kullanıcılar
      </Link>

      {/* HERO */}
      <header className="md-card ukd-hero">
        <div className="ukd-hero-avatar" aria-hidden="true">
          {user?.photo ? <img src={user.photo} alt="" /> : avatarInitial}
        </div>
        <div className="ukd-hero-main">
          <h1 className="ukd-hero-name">{formatPersonNameTr(user)}</h1>
          <div className="ukd-hero-username">@{user?.username}</div>
          <div className="ukd-hero-chips">
            {user?.global_role?.name ? (
              <span className="md-chip md-chip-info">{user.global_role.name}</span>
            ) : null}
            <span
              className={`md-chip ${user?.is_active !== false ? "md-chip-success" : "md-chip-danger"}`}
            >
              {user?.is_active !== false ? "Aktif" : "Pasif"}
            </span>
            {user?.is_superuser ? (
              <span className="md-chip md-chip-danger ukd-chip-super">
                <ShieldExclamationIcon width={13} height={13} /> Süper kullanıcı
              </span>
            ) : null}
          </div>
        </div>
        <div className="ukd-hero-meta">
          <span className="ukd-meta-item">
            <EnvelopeIcon width={15} height={15} />
            {safeText(user?.email)}
          </span>
          <span className="ukd-meta-item">
            <PhoneIcon width={15} height={15} />
            {safeText(user?.phone_number)}
          </span>
          <span className="ukd-meta-item">
            <BuildingOffice2Icon width={15} height={15} />
            {memberships.length} birim
          </span>
          <span className="ukd-meta-item">
            <ClockIcon width={15} height={15} />
            {user?.last_login ? <TrDateTime value={user.last_login} /> : "Hiç giriş yok"}
          </span>
        </div>
      </header>

      {/* SEKMELER */}
      <div className="ukd-tabs" role="tablist" aria-label="Kullanıcı detay sekmeleri">
        {TABS.map((t) => {
          const active = activeTab === t.key;
          const Icon = t.Icon;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              className={`ukd-tab${active ? " active" : ""}`}
              onClick={() => setTab(t.key)}
            >
              <Icon width={16} height={16} />
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* PANEL — Roller & Birimler */}
      {activeTab === "roller" && (
        <section className="md-card ukd-panel">
          <div className="md-card-header">
            <div>
              <h2 className="md-card-title">Birim ve rol atamaları</h2>
              <p className="md-card-subtitle">
                {canAssign
                  ? "Kullanıcının hangi birimde hangi rolle çalıştığını yönetin."
                  : "Görüntüleme modu — atama için 'users.assign_role' izni gerekir."}
              </p>
            </div>
          </div>

          {memberships.length === 0 ? (
            <div className="skd-empty">Bu kullanıcının birim ataması yok.</div>
          ) : (
            <ul className="skd-mlist">
              {memberships.map((m) => (
                <li key={m.id} className="skd-mrow">
                  <span className="skd-mrow-unit">{unitName(m.unit)}</span>
                  <select
                    className="md-select skd-mrow-role"
                    value={m.role || ""}
                    disabled={!canAssign || busyId === m.id}
                    onChange={(e) => changeRole(m, e.target.value)}
                    aria-label="Rol"
                  >
                    <option value="">Rol yok</option>
                    {roles.map((r) => (
                      <option key={r.id} value={r.code}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                  <span className={`md-chip ${m.is_active ? "md-chip-success" : "md-chip-danger"}`}>
                    {m.is_active ? "Aktif" : "Pasif"}
                  </span>
                  {canAssign && (
                    <button
                      className="btn-md btn-md-ghost skd-mrow-btn"
                      disabled={busyId === m.id}
                      onClick={() => toggleActive(m)}
                    >
                      {m.is_active ? "Pasifle" : "Etkinleştir"}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {canAssign && (
            <form className="skd-add" onSubmit={addMembership}>
              <select
                className="md-select"
                value={newUnit}
                onChange={(e) => setNewUnit(e.target.value)}
                aria-label="Birim seç"
                required
              >
                <option value="">Birim seç…</option>
                {units.map((u) => (
                  <option key={u.id} value={u.id}>
                    {"— ".repeat(u.depth)}
                    {u.name}
                  </option>
                ))}
              </select>
              <select
                className="md-select"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                aria-label="Rol seç"
              >
                <option value="">Rol seç…</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.code}>
                    {r.name}
                  </option>
                ))}
              </select>
              <button type="submit" className="btn-md btn-md-primary skd-add-btn" disabled={adding || !newUnit}>
                <PlusIcon width={18} height={18} /> Ekle
              </button>
            </form>
          )}
        </section>
      )}

      {/* PANEL — Özel İzinler */}
      {activeTab === "ozel" && canAssignPerm && (
        <section className="md-card ukd-panel">
          <div className="md-card-header">
            <div>
              <h2 className="md-card-title">Özel izinler (kişiye özel)</h2>
              <p className="md-card-subtitle">
                Bir birimde rolün üstüne ekstra izin verin (allow) veya rolden gelen izni
                kapatın (deny). Öncelik: <b>deny &gt; allow &gt; rol</b>.
              </p>
            </div>
          </div>
          {memberships.length === 0 ? (
            <div className="skd-empty">Önce bir birim ataması ekleyin.</div>
          ) : (
            <>
              <select
                className="md-select"
                value={ozelUnit}
                onChange={(e) => setOzelUnit(e.target.value)}
                aria-label="Özel izin için birim üyeliği"
              >
                <option value="">Birim üyeliği seç…</option>
                {memberships.map((m) => (
                  <option key={m.id} value={m.id}>
                    {unitName(m.unit)} — {rolesByCode[m.role]?.name || "Rol yok"}
                  </option>
                ))}
              </select>
              {ozelMembership && (
                <OzelIzinlerEditor
                  key={ozelMembership.id}
                  membership={ozelMembership}
                  actions={actions}
                  isSuperUser={isSuperUser}
                  onSaved={load}
                />
              )}
            </>
          )}
        </section>
      )}

      {/* PANEL — Efektif izin simülatörü */}
      {activeTab === "simulator" && (
        <section className="md-card ukd-panel">
          <div className="md-card-header">
            <div>
              <h2 className="md-card-title">Efektif izin simülatörü</h2>
              <p className="md-card-subtitle">
                Seçilen birimde fiili izinler: <span className="skd-dot skd-dot-green" /> rolden ·{" "}
                <span className="skd-dot skd-dot-blue" /> kişiye eklenen ·{" "}
                <span className="skd-dot skd-dot-red" /> kapatılan.
              </p>
            </div>
          </div>
          <div className="skd-preview-controls">
            <select
              className="md-select"
              value={previewUnit}
              onChange={(e) => setPreviewUnit(e.target.value)}
              aria-label="Önizleme birimi"
            >
              <option value="">Bağlamsız (global)</option>
              {units.map((u) => (
                <option key={u.id} value={u.id}>
                  {"— ".repeat(u.depth)}
                  {u.name}
                </option>
              ))}
            </select>
            <button className="btn-md btn-md-outline" onClick={runPreview} disabled={previewLoading}>
              {previewLoading ? "Hesaplanıyor…" : "İzinleri göster"}
            </button>
          </div>

          {preview !== null && (
            <div className="skd-sim">
              {breakdown.hasMembership ? (
                <>
                  <SimGrup baslik="Rolden gelen" cls="green" codes={breakdown.green} />
                  <SimGrup baslik="Kişiye eklenen" cls="blue" codes={breakdown.blue} />
                  <SimGrup baslik="Kapatılan (deny)" cls="red" codes={breakdown.red} strike />
                </>
              ) : (
                <SimGrup baslik="Efektif izinler" cls="green" codes={preview} />
              )}

              <div className="skd-sim-pages">
                <div className="skd-sim-pages-title">Bu kullanıcının göreceği sayfalar</div>
                {previewPages.length === 0 ? (
                  <div className="skd-empty">Bu bağlamda açılan sayfa yok.</div>
                ) : (
                  <ul className="skd-preview-list">
                    {previewPages.map(([group, items]) => (
                      <li key={group} className="skd-preview-group">
                        <span className="skd-preview-group-name">{group}</span>
                        <span className="skd-preview-pages-chips">
                          {items.map((it) => (
                            <span key={it.page} className="md-chip skd-perm">
                              {it.href.split("/").pop() || it.page}
                            </span>
                          ))}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </section>
      )}

      {/* PANEL — Hesap & Güvenlik */}
      {activeTab === "guvenlik" && (canResetPw || canViewLogs) && (
        <section className="md-card ukd-panel">
          <div className="md-card-header">
            <h2 className="md-card-title">Hesap / Güvenlik</h2>
          </div>

          {canResetPw && (
            <div className="skd-pw">
              <div className="ukd-sec-actions">
                <button className="btn-md btn-md-outline" onClick={doResetPassword} disabled={pwBusy}>
                  <KeyIcon width={16} height={16} />
                  {pwBusy ? "Sıfırlanıyor…" : "Şifre sıfırla (göster)"}
                </button>
                <button className="btn-md btn-md-primary" onClick={doSendSms} disabled={smsBusy}>
                  <DevicePhoneMobileIcon width={16} height={16} />
                  {smsBusy ? "Gönderiliyor…" : "SMS ile şifre gönder"}
                </button>
              </div>

              {pwErr && <div className="skd-error">{pwErr}</div>}
              {pwResult && (
                <div className="skd-pw-box">
                  <span>Geçici parola (tekrar gösterilmez): </span>
                  <code>{pwResult.generated_password}</code>
                </div>
              )}
              {smsMsg && (
                <div className={smsMsg.ok ? "skd-pw-box" : "skd-error"}>{smsMsg.text}</div>
              )}
            </div>
          )}

          {canViewLogs && (
            <div className="skd-logs">
              <div className="ukd-logs-title">Giriş / oturum geçmişi</div>
              {logs === null ? (
                <div className="skd-empty-inline">
                  {logsBusy ? "Yükleniyor…" : "Log kaydı bekleniyor…"}
                </div>
              ) : logs.length === 0 ? (
                <div className="skd-empty-inline">Giriş kaydı yok.</div>
              ) : (
                <ul className="skd-log-list">
                  {logs.map((o) => (
                    <li key={o.id} className="skd-log-row">
                      <span>{o.cihaz}</span>
                      <span className="skd-log-ip">{o.ip || "—"}</span>
                      <span>
                        <TrDateTime value={o.giris_zamani} />
                      </span>
                      <span className={`md-chip ${o.aktif ? "md-chip-success" : ""}`}>
                        {o.aktif ? "Aktif" : o.kapanma || "Kapandı"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function SimGrup({ baslik, cls, codes, strike = false }) {
  if (!codes || codes.length === 0) return null;
  return (
    <div className="skd-sim-grup">
      <div className="skd-sim-grup-title">
        {baslik} <span className="skd-sim-count">{codes.length}</span>
      </div>
      <div className="skd-perms">
        {codes.map((code) => (
          <span
            key={code}
            className={`md-chip skd-perm skd-perm-${cls}${strike ? " skd-perm-strike" : ""}`}
          >
            {code}
          </span>
        ))}
      </div>
    </div>
  );
}

// Bir üyelik için allow/deny editörü. SENSITIVE allow superuser-only (backend R10 de zorlar).
function OzelIzinlerEditor({ membership, actions, isSuperUser, onSaved }) {
  const [allow, setAllow] = useState(() => new Set(membership.allow_actions || []));
  const [deny, setDeny] = useState(() => new Set(membership.deny_actions || []));
  const [q, setQ] = useState("");
  const [pick, setPick] = useState("");
  const [saving, setSaving] = useState(false);

  const origAllow = useMemo(() => new Set(membership.allow_actions || []), [membership]);
  const origDeny = useMemo(() => new Set(membership.deny_actions || []), [membership]);
  const dirty = !setsEqual(allow, origAllow) || !setsEqual(deny, origDeny);

  const byCode = useMemo(() => Object.fromEntries(actions.map((a) => [a.code, a])), [actions]);
  const filtered = useMemo(() => {
    const n = q.trim().toLocaleLowerCase("tr");
    return actions
      .filter((a) => !n || `${a.name} ${a.code}`.toLocaleLowerCase("tr").includes(n))
      .slice(0, 40);
  }, [actions, q]);

  const addAllow = () => {
    if (!pick) return;
    if (byCode[pick]?.is_sensitive && !isSuperUser) {
      toast.error("Kritik izin yalnız süper kullanıcı tarafından verilebilir.");
      return;
    }
    setAllow((p) => new Set(p).add(pick));
    setDeny((p) => {
      const n = new Set(p);
      n.delete(pick);
      return n;
    });
  };
  const addDeny = () => {
    if (!pick) return;
    setDeny((p) => new Set(p).add(pick));
    setAllow((p) => {
      const n = new Set(p);
      n.delete(pick);
      return n;
    });
  };
  const rm = (setter) => (code) =>
    setter((p) => {
      const n = new Set(p);
      n.delete(code);
      return n;
    });

  const save = async () => {
    setSaving(true);
    try {
      await updateMembership(membership.id, {
        allow_actions: [...allow],
        deny_actions: [...deny],
      });
      toast.success("Özel izinler kaydedildi.");
      await onSaved();
    } catch (ex) {
      toast.error(backendError(ex, "Kaydedilemedi."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="skd-ozel">
      <div className="skd-ozel-lists">
        <div>
          <div className="skd-ozel-label skd-ozel-label-allow">Eklenen (allow)</div>
          <div className="skd-perms">
            {[...allow].length === 0 && <span className="skd-empty-inline">yok</span>}
            {[...allow].sort().map((c) => (
              <span key={c} className="md-chip skd-perm skd-perm-blue">
                {c}
                <button type="button" className="skd-chip-x" onClick={() => rm(setAllow)(c)} aria-label="kaldır">
                  <XMarkIcon width={12} height={12} />
                </button>
              </span>
            ))}
          </div>
        </div>
        <div>
          <div className="skd-ozel-label skd-ozel-label-deny">Kapatılan (deny)</div>
          <div className="skd-perms">
            {[...deny].length === 0 && <span className="skd-empty-inline">yok</span>}
            {[...deny].sort().map((c) => (
              <span key={c} className="md-chip skd-perm skd-perm-red skd-perm-strike">
                {c}
                <button type="button" className="skd-chip-x" onClick={() => rm(setDeny)(c)} aria-label="kaldır">
                  <XMarkIcon width={12} height={12} />
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="skd-ozel-add">
        <input
          className="md-input"
          placeholder="İzin ara (ad/kod)"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="İzin ara"
        />
        <select className="md-select" value={pick} onChange={(e) => setPick(e.target.value)} aria-label="İzin seç">
          <option value="">İzin seç…</option>
          {filtered.map((a) => (
            <option key={a.code} value={a.code}>
              {a.name} ({a.code}){a.is_sensitive ? " ⚠" : ""}
            </option>
          ))}
        </select>
        <div className="skd-ozel-add-btns">
          <button type="button" className="btn-md btn-md-outline" onClick={addAllow} disabled={!pick}>
            + İzin ver
          </button>
          <button type="button" className="btn-md btn-md-ghost" onClick={addDeny} disabled={!pick}>
            − Yasakla
          </button>
        </div>
      </div>

      {dirty && (
        <div className="skd-ozel-save">
          <button className="btn-md btn-md-primary" onClick={save} disabled={saving}>
            {saving ? "Kaydediliyor…" : "Özel izinleri kaydet"}
          </button>
        </div>
      )}
    </div>
  );
}
